import type { ReactNode } from "react";
import type { DisclosureProps } from "react-aria-components";
import { Button, Disclosure, DisclosurePanel } from "react-aria-components";

import { ExpandableIndicator } from "@jetblue/kit/expandable-indicator";

import { FOCUS_OUTLINE_STYLES } from "../button/button-styles";
import { cn } from "../../utilities/tailwind";

export const ExpansionPanelVariantsArray = ["primary", "secondary"] as const;
const expansionPanelStyles = {
  button: {
    base: cn(
      "group/expandable-ui flex w-full items-center justify-between p-4 text-start antialiased",
      FOCUS_OUTLINE_STYLES,
    ),
    primary: cn(
      "bg-primary-variant text-m-heading font-normal text-on-primary hover:bg-primary focus-visible:bg-primary data-[is-expanded=true]:bg-surface-container data-[is-expanded=true]:text-on-surface",
    ),
    secondary: cn(
      "border-t border-outline bg-surface text-xs-heading-extra-bold text-primary-variant hover:bg-state-surface-container-hover-variant focus-visible:bg-state-surface-container-hover-variant",
    ),
  },
  contentSection: {
    base: cn("px-4 opacity-0 transition-all duration-300"),
    isOpen: cn("pb-4 opacity-100"),
    primary: cn("bg-surface-container"),
    secondaryBorder: cn("border-b border-outline"),
  },
  expandableIcon: {
    base: cn("size-5 text-inherit"),
  },
};

type ExpansionPanelWithContent = {
  children?: never;
  /**
   * Content to be displayed when the expandable section is open. In place of the `content` property, developers may pass `children` to this component instead.
   */
  content: ReactNode;
};

type ExpansionPanelWithChildren = {
  /**
   * In place of the `content` property developers may pass `children` to this component instead.
   */
  children: ReactNode;
  content?: never;
};

export type ExpansionPanelProperties = Omit<
  DisclosureProps,
  | "children"
  | "className"
  | "defaultExpanded"
  | "isDisabled"
  | "isExpanded"
  | "onExpandedChange"
  | "slot"
  | "style"
> & {
  /**
   * Add or overwrite styles for the button.
   */
  buttonClassName?: string | undefined;

  /**
   * Add or overwrite styles for the root div.
   */
  className?: string | undefined;

  /**
   * Add or overwrite styles for the content section.
   */
  contentClassName?: string | undefined;

  /**
   * Add or overwrite styles for the `ExpandableIndicator`.
   */
  expandableIndicatorClassName?: string | undefined;

  /**
   * The value of the `ExpansionPanel`'s button content.
   */
  header: ReactNode;

  /**
   * Unique value to be used for accessibility purposes.
   */
  id: string;

  /**
   * Boolean value which dictates whether or not the collapsible section is open or closed.
   */
  isExpanded: boolean;

  /**
   * Boolean value when a panel is the final panel in a list.
   */
  isLastPanel?: boolean | undefined;

  /**
   * Callback which is invoked when the button is clicked.
   */
  setIsExpanded: (id: string) => void;

  /**
   * Basic variants that control styling for the trigger button.
   */
  variant?: (typeof ExpansionPanelVariantsArray)[number] | undefined;
} & (ExpansionPanelWithContent | ExpansionPanelWithChildren);
export const ExpansionPanel = ({
  buttonClassName = "",
  children,
  className = "",
  content,
  contentClassName = "",
  expandableIndicatorClassName = "",
  header,
  id,
  isExpanded,
  isLastPanel,
  setIsExpanded,
  variant = "primary",
  ...properties
}: ExpansionPanelProperties) => {
  const buttonTwClassName = cn(
    expansionPanelStyles.button.base,
    { [expansionPanelStyles.button.primary]: variant === "primary" },
    { [expansionPanelStyles.button.secondary]: variant === "secondary" },
    buttonClassName,
  );
  const contentTwClassName = cn(
    expansionPanelStyles.contentSection.base,
    { [expansionPanelStyles.contentSection.isOpen]: isExpanded },
    { [expansionPanelStyles.contentSection.primary]: variant === "primary" },
    contentClassName,
  );
  const expandableTwClassName = cn(
    expansionPanelStyles.expandableIcon.base,
    expandableIndicatorClassName,
  );

  return (
    <Disclosure className={className} isExpanded={isExpanded} {...properties}>
      <Button
        aria-expanded={isExpanded}
        className={buttonTwClassName}
        data-is-expanded={isExpanded}
        onPress={() => {
          setIsExpanded(id);
        }}
      >
        <div className="flex w-full items-center justify-between">
          {header}

          <span className="pl-2">
            <ExpandableIndicator className={expandableTwClassName} />
          </span>
        </div>
      </Button>

      <DisclosurePanel
        className={contentTwClassName}
        data-is-last={isLastPanel}
        data-is-visible={isExpanded}
      >
        {children ?? content}
      </DisclosurePanel>

      {variant === "secondary" && isLastPanel && (
        <div className={expansionPanelStyles.contentSection.secondaryBorder} />
      )}
    </Disclosure>
  );
};
ExpansionPanel.displayName = "ExpansionPanel";
